import React, { useEffect, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { Searchbar, Text } from "react-native-paper";
import { useTranslation } from "react-i18next";
import { listenCompanyDocuments } from "../../services/firestoreServices";
import { Folder, File } from "./types";
import FolderCard from "./components/FolderCard";
import CreateFolderDialog from "./components/CreateFolderDialog";
import DriveFAB from "./components/DriveFAB";
import RecentFilesSection from "./components/RecentFilesSection";
import { useFolderManagement } from "./hooks/useFolderManagement";
import { useFileManagement } from "./hooks/useFileManagement";
import { downloadDriveFile } from "./utils/downloadDriveFile";
import { getFileModifiedTimestamp } from "./utils/formatTime";
import RenameFileDialog from "./components/RenameFileDialog";

interface MyDriveScreenProps {
  navigation: any;
}

export default function DriveScreen({ navigation }: MyDriveScreenProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [folders, setFolders] = useState<Folder[]>([]);
  const [recentFiles, setRecentFiles] = useState<File[]>([]);
  const { t } = useTranslation();

  const folderManagement = useFolderManagement({
    existingFolders: folders,
  });
  const fileManagement = useFileManagement({});

  // Listen to root folders from Firestore (folders without parentFolderId)
  useEffect(() => {
    const unsubscribe = listenCompanyDocuments(
      "drive_folders",
      (data: Folder[]) => {
        const rootFolders = data
          .filter((f) => !f.parentFolderId)
          .sort((a, b) => a.name.localeCompare(b.name));
        setFolders(rootFolders);
      },
    );

    return () => unsubscribe?.();
  }, []);

  // Listen to files from Firestore
  useEffect(() => {
    const unsubscribe = listenCompanyDocuments(
      "drive_files",
      (data: File[]) => {
        // Sort by updatedAt so the newest file activity appears first.
        const sorted = [...data].sort(
          (a, b) => getFileModifiedTimestamp(b) - getFileModifiedTimestamp(a),
        );
        setRecentFiles(sorted.slice(0, 10)); // Show only 10 most recent files
      },
      {
        orderBy: [{ field: "updatedAt", direction: "desc" }],
        limit: 10,
      },
    );

    return () => unsubscribe?.();
  }, []);

  const handleFolderPress = (folder: Folder) => {
    navigation.navigate("FolderContents", { folder });
  };

  const handleFileOpen = async (file: File) => {
    try {
      await downloadDriveFile(file);
    } catch (error) {
      console.error("Failed to open file:", error);
    }
  };

  const handleFileShare = (file: File) => {
    console.log("Share file", file.id);
  };

  const handleFileOpenDetails = (file: File) => {
    navigation.navigate("FileDetails", { file });
  };

  return (
    <View style={styles.container}>
      <ScrollView style={styles.content}>
        <View style={styles.searchContainer}>
          <Searchbar
            placeholder={t("Search in Drive")}
            onChangeText={setSearchQuery}
            value={searchQuery}
            style={styles.searchbar}
          />
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text variant="labelLarge" style={styles.sectionTitle}>
              {t("Folders")}
            </Text>
            {/*
            <Text variant="labelMedium" style={styles.viewAll}>
              {t("View All")}
            </Text>
            */}
          </View>

          <View style={styles.foldersGrid}>
            {folders.map((folder) => (
              <FolderCard
                key={folder.id}
                folder={folder}
                onPress={handleFolderPress}
                onEdit={folderManagement.openEditDialog}
                onDelete={folderManagement.openDeleteDialog}
              />
            ))}
          </View>
        </View>

        <RecentFilesSection
          files={recentFiles}
          onFileOpen={handleFileOpen}
          onFileDownload={handleFileOpen}
          onFileShare={handleFileShare}
          onFileRename={fileManagement.openRenameDialog}
          onFileDelete={fileManagement.openDeleteDialog}
          onFileViewDetails={handleFileOpenDetails}
        />

        <View style={styles.bottomSpacer} />
      </ScrollView>

      <CreateFolderDialog
        visible={folderManagement.dialogVisible}
        folderName={folderManagement.folderName}
        error={folderManagement.error}
        mode={folderManagement.editingFolder ? "edit" : "create"}
        onChangeName={folderManagement.handleNameChange}
        onConfirm={folderManagement.saveFolder}
        onCancel={folderManagement.closeNameDialog}
      />

      <RenameFileDialog
        visible={fileManagement.renameDialogVisible}
        fileName={fileManagement.fileName}
        error={fileManagement.renameError}
        onChangeName={fileManagement.handleFileNameChange}
        onConfirm={fileManagement.saveRenamedFile}
        onCancel={fileManagement.closeRenameDialog}
      />

      <DriveFAB onCreateFolder={folderManagement.openCreateDialog} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  searchContainer: {
    padding: 16,
  },
  searchbar: {
    elevation: 1,
  },
  section: {
    marginTop: 8,
    paddingHorizontal: 16,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: "600",
    opacity: 0.6,
  },
  viewAll: {
    color: "#2094f3",
    fontWeight: "500",
  },
  foldersGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 16,
  },
  bottomSpacer: {
    height: 120,
  },
});
